import { ReactNode, useEffect } from 'react';
import { ColumnMeta, RowData } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { NavigableError } from 'services/errorsNavigation/errorsNavigation';
import { classNames } from 'support/helpers/generic/generic';

type ViewWrapperCellProps = {
  children: ReactNode;
  className?: string;
  fieldId: string;
  isHighlighted: boolean;
  meta: ColumnMeta<RowData, unknown> | undefined;
};
export const ViewWrapperCell = ({ className, isHighlighted, fieldId, children, meta }: ViewWrapperCellProps) => {
  const { service, ErrorModalComponent, useErrorModal } = useErrorContext<NavigableError>();
  const error = service.useErrorById(fieldId);
  const numberOfErrors = service.useNumberOfActiveErrors();
  const setCurrentError = service.setCurrentError;
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();

  useEffect(() => {
    if (error && showErrorModal) {
      setCurrentError(error);
    }
  }, [error, setCurrentError, showErrorModal]);

  return (
    <>
      <div
        data-field={fieldId}
        tabIndex={error ? 0 : undefined}
        onFocus={() => setShowErrorModal(true)}
        onBlur={() => setShowErrorModal(false)}
        className={classNames(
          'border w-full h-full flex justify-between gap-2 items-center px-3 outline-none',
          className,
          {
            'text-red-700 underline border-bg-red-50 bg-red-50 py-2.5 cursor-pointer hover:bg-red-100': error,
            'py-3': !error,
            'bg-amber-100': isHighlighted,
            'border-red-500': showErrorModal,
            'border-transparent': !showErrorModal,
          },
        )}
        ref={refs.setReference}
      >
        {children}
      </div>
      {showErrorModal && ErrorModalComponent && (
        <ErrorModalComponent
          error={error}
          viewMode
          ref={refs.setFloating}
          style={floatingStyles}
          numberOfErrors={numberOfErrors}
          fieldDescription={meta?.description}
        />
      )}
    </>
  );
};
